import React, { useState } from "react";

import useUpdateTaskV3 from "@fartherfinance/frontend/api/Tasks/hooks/useUpdateTask.v3";
import { TaskDetailsV3 } from "@fartherfinance/frontend/api/Tasks/Types";

import { useTaskPermissions } from "../../../taskPermissionsContext";
import { isTaskFieldEditingRestricted } from "../../../utils";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import Text from "@src/sharedComponents/Typography/Text";
import { extractAxiosErrorMessage } from "@src/utils/axios";

import styles from "./TaskDescription.module.css";

interface TaskDescriptionProps {
  task: TaskDetailsV3;
}

const TaskDescription: React.FC<TaskDescriptionProps> = ({ task }) => {
  const [descriptionValue, setDescriptionValue] = useState<string | null>(
    task.description
  );

  const auth = useAdvisorRequestAuth();
  const callUpdateTaskV3 = useUpdateTaskV3(auth);
  const statusNotification = useStatusNotification();

  const { taskPermissions } = useTaskPermissions();
  const { isAllowedToUpdate: isAllowedToEditTask } = taskPermissions(task);

  const handleUpdateTask = async (
    newTaskDescription: string
  ): Promise<void> => {
    if (newTaskDescription === task.description) {
      return;
    }

    try {
      await callUpdateTaskV3({
        taskId: task.id,
        request: {
          description: {
            value: newTaskDescription,
          },
        },
      });

      trackEvent({ name: "Advisor Tasks Update" });
    } catch (error) {
      setDescriptionValue(task.description);
      statusNotification(
        extractAxiosErrorMessage(error, "Failed to update task."),
        "Error"
      );
    }
  };

  return (
    <Text
      className={styles.description}
      variant="body"
      color="textSubtle"
      contentEditable={
        isAllowedToEditTask &&
        isTaskFieldEditingRestricted("description", task) === false
      }
      suppressContentEditableWarning={true}
      spellCheck={false}
      onBlur={(event) => handleUpdateTask(event.target.innerText)}
    >
      {descriptionValue}
    </Text>
  );
};

export default TaskDescription;
